<template>
  <TitledSection class="flow-selector" :align-content-left="true">
    <template v-slot:icon>
      <Icon :icon="icon" class="icon" />
    </template>
    <template v-slot:title>
      {{ title }}
    </template>
    <template v-slot:subtitle>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            {{ subtitle }}
          </div>
          <div v-if="tooltip" class="level-item">
            <n-tooltip :text="tooltip" position="top" />
          </div>
        </div>
      </div>
    </template>

    <div class="flow-selector__main">
      <div class="flow-selector__list">
        <Draggable v-if="reorderable" v-model="flows" @end="updatePriorities" item-key="id">
          <template #item="{ element: flow }">
            <div class="flow-selector__list-item" :key="flow.id">
              <span class="icon draggable">
                <DragIcon color="#A9A9A9" class="draggable" />
              </span>
              <label :for="`toggle-${flow.id}`" class="flow-selector__toggle-label">{{ flow.name }}</label>
              <n-toggle
                v-model="flow.isSelected"
                :label="flow.name"
                :hideLabel="true"
                :id="`toggle-${flow.id}`"
                @input="handleFlowSelectedChange"
              ></n-toggle>
            </div>
          </template>
        </Draggable>
        <div v-else class="flow-selector__list-item" v-for="flow in flows" :key="flow.id">
          <label :for="`toggle-${flow.id}`" class="flow-selector__toggle-label">{{ flow.name }}</label>
          <n-toggle
            :label="flow.name"
            v-model="flow.isSelected"
            @input="handleFlowSelectedChange"
            :hideLabel="true"
            :id="`toggle-${flow.id}`"
          ></n-toggle>
        </div>
      </div>
    </div>
  </TitledSection>
</template>

<script lang="ts" setup>
import Draggable from 'vuedraggable';
import DragIcon from '@/shared/components/DragIcon.vue';
import { FlowSelectorItemViewModel } from '../../models/flowSelectorItemViewModel';
import appInsights from '@/shared/services/appInsights';
import TitledSection from '@/shared/components/TitledSection.vue';
import Icon from '@/shared/icons/Icon.vue';
import { computed, PropType, ref } from 'vue';
import { useVModel } from '@vueuse/core';
import { useStore } from 'vuex';

const props = defineProps({
  modelValue: {
    type: Array as PropType<FlowSelectorItemViewModel[]>,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  subtitle: {
    type: String,
    required: true,
  },
  icon: {
    type: String as PropType<'FlowIcon' | 'AnswerIcon'>,
    required: true,
  },
  tooltip: {
    type: String,
    default: '',
  },
  reorderable: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits<{
  (e: 'update:modelValue', value: FlowSelectorItemViewModel[]): void;
}>();
const flows = useVModel(props, 'modelValue', emit);

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);

function handleFlowSelectedChange() {
  appInsights.trackEvent({ name: 'Bot Preview: Flow selected changed' }, { workspaceId: currentWorkspace.value.id });
  setPriorities();
}

function updatePriorities() {
  appInsights.trackEvent({ name: 'Bot Preview: Flow priorities changed' }, { workspaceId: currentWorkspace.value.id });
  setPriorities();
}

function setPriorities() {
  let selectedFlowCount = flows.value.filter((x) => x.isSelected).length;
  flows.value.forEach((flow) => {
    flow.priority = flow.isSelected ? selectedFlowCount-- : 0;
  });
}
</script>

<style lang="scss" scoped>
.flow-selector {
  :deep(.titled-section__heading) {
    .header {
      padding: 1rem 0.25rem 0.25rem 0.25rem;
    }
  }
  :deep(.titled-section__content) {
    max-height: unset;
  }

  &__list-item {
    margin: 0.5rem 1rem;
    display: flex;
    align-items: center;
    .icon {
      margin-right: 0.5rem;
      cursor: move;
      align-self: center;
      position: relative;
      height: 1em;
      width: 1em;
      left: -6px;
    }
    :deep(svg) {
      height: 1em;
      width: 1em;
    }
    :deep(.n-input) {
      .toggle__input-container {
        margin-left: 1rem;
        .toggle {
          width: 1.5rem;
          height: 0.75rem;
          .toggle-inner {
            top: 0.125rem;
            left: 0.125rem;
            width: 0.5rem;
            height: 0.5rem;
          }
        }
        input[type='checkbox']:checked + .toggle {
          .toggle-inner {
            left: 0.8125rem;
          }
        }
      }
    }
  }
  &__toggle-label {
    min-width: 0;
    margin-right: 0.5rem;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 0.8rem;
    flex: 1 0;
  }
}
</style>
